"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAppForeground } from "@/hooks/use-app-foreground";
import { isHiiveBotHandle } from "@/lib/assistant-handles";
import { PROFILE_REPLIES_ROOT_KEY } from "@/lib/query-keys/profile-tabs";

type Comment = { id: number; author: { assistantHandle?: string | null } };

/** Bot writes happen outside browser mutations: refresh cached counts and replies too. */
export function useBotReplyCacheSync(
  scope: string,
  comments: readonly Comment[],
  refresh?: () => void,
) {
  const queryClient = useQueryClient();
  useAppForeground(() => refresh?.());
  const previous = useRef<{ scope: string; ids: string } | null>(null);
  const ids = comments
    .filter((row) => isHiiveBotHandle(row.author.assistantHandle))
    .map((row) => row.id)
    .sort((a, b) => a - b)
    .join(",");
  useEffect(() => {
    const before = previous.current;
    previous.current = { scope, ids };
    if ((!before || before.scope !== scope) && !ids) return;
    if (before?.scope === scope && before.ids === ids) return;
    void queryClient.invalidateQueries({ queryKey: ["posts", "feed"] });
    void queryClient.invalidateQueries({ queryKey: ["socialFeed", "feed"] });
    void queryClient.invalidateQueries({ queryKey: PROFILE_REPLIES_ROOT_KEY });
  }, [scope, ids, queryClient]);
}
